import { createSlice } from '@reduxjs/toolkit';
import { addHours, addMinutes, format, parse, startOfHour } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';
import initialState, { createInitialAllTasksState } from './initialState';
import {
  createTask,
  getAgents,
  getContacts,
  getTaskFilterAgents,
  getTaskFilterContacts,
  getTaskFilterPropertyById,
  getTaskFilterContactById,
  changeTaskStatusFromAllTasks,
  getProperties,
  getTaskFilterProperties,
  getPaginatedAllTaskSection,
  getRightbarTasks,
  completeTaskFromRightbar,
  getPaginatedFilteredTasks,
  changeTaskStatusFromFilteredTasks,
  editTask,
  getAgentConflicts,
} from './services';
import { filterDuplicateElements } from '@/store/slices/components/calendarEvents/utils';
import { COMPLETED_TASK_STATUS } from '@/components/Tasks/constants/status';
import {
  markCompleting,
  markUncompleting,
  removeTaskById,
  unmarkCompleting,
  unmarkUncompleting,
} from '@/components/Tasks/utils/taskArray';
import {
  appendTaskSections,
  getResponseTaskSections,
  normalizeTaskSections,
} from '@/components/Tasks/utils/taskSections';

const normalizeTaskContextualItem = (item, itemKey) => {
  if (itemKey === 'properties') return { id: item.id, address: item.address };
  if (itemKey === 'contacts') return { id: item.id, name: item.name };
  if (itemKey === 'agents') return { id: item.id, full_name: item.full_name };

  return { id: item?.id, text: '-' };
};

const getSelectedTaskContextualData = (itemKey, results) =>
  (results ?? [])
    .flatMap((response) => response?.[itemKey] ?? [])
    .map((item) => normalizeTaskContextualItem(item, itemKey));

export const TasksSlice = createSlice({
  name: 'Tasks',
  initialState,
  reducers: {
    setTasks(state, action) {
      const { key, subkey, value } = action.payload;

      if (subkey === 'isAddTimeActive') {
        state.newTask.isAddTimeActive = value;

        if (value) {
          const now = new Date();
          const roundedEndTime = startOfHour(addHours(now, 1));

          state.newTask.startTime = format(
            addMinutes(roundedEndTime, -30),
            'HH:mm'
          );
          state.newTask.endTime = format(roundedEndTime, 'HH:mm');
        } else {
          state.newTask.startTime = initialState.newTask.startTime;
          state.newTask.endTime = initialState.newTask.endTime;
        }

        return;
      }

      if (subkey) {
        state[key][subkey] = value;
        return;
      }

      state[key] = value;
    },
    resetModalTask(state) {
      state.newTask = initialState.newTask;
      state.originalNewTask = null;
    },
    showModalTask(state, action) {
      state.showModal = action.payload;
    },
    showModalTaskToEdit(state, action) {
      function getContactsToAssign(contactsToAssign) {
        if (!contactsToAssign) return [];

        return contactsToAssign.map((contact) => ({
          id: contact.id,
          name: contact.name,
        }));
      }
      function getPropertyToAssign(propertyToAssign) {
        if (!propertyToAssign) return [];

        return [
          {
            id: propertyToAssign.id,
            address: propertyToAssign.name,
          },
        ];
      }
      function getAgentsToAssign(agentsToAssign) {
        if (!agentsToAssign) return [];

        return agentsToAssign.map((agent) => ({
          id: agent.id,
          full_name: agent.name,
        }));
      }

      const {
        id,
        title,
        date,
        contacts,
        property,
        responsibles,
        display_time,
        end_time,
      } = action.payload;

      state.showModal = true;

      state.newTask = {
        ...state.newTask,
        id,
        title,
        date: parse(date, 'yyyy-MM-dd', new Date()),
        startTime: display_time ? display_time.slice(0, 5) : null,
        endTime: end_time ? end_time.slice(0, 5) : null,
        isAddTimeActive: Boolean(end_time),
        contacts: {
          ...state.newTask.contacts,
          selected: getContactsToAssign(contacts),
        },
        properties: {
          ...state.newTask.properties,
          selected: getPropertyToAssign(property),
        },
        agents: {
          ...state.newTask.agents,
          selected: getAgentsToAssign(responsibles),
        },
        isEditing: true,
      };

      state.originalNewTask = cloneDeep(state.newTask);
    },
    selectTaskTab(state, action) {
      const tabId = action.payload;

      state.tabs = state.tabs.map((tab) => ({
        ...tab,
        active: tab.id === tabId,
      }));
    },
    resetAllTasks(state) {
      state.allTasks = createInitialAllTasksState();
      state.allTasks.pending.loadingMore = true;
      state.allTasks.completed.loadingMore = true;
    },
    setNewTaskContextualData(state, action) {
      const { itemKey, results } = action.payload;

      state.newTask[itemKey] = {
        data: [],
        selected: getSelectedTaskContextualData(itemKey, results),
        loading: false,
        error: null,
      };
    },
    setNewTaskTimeRange(state, action) {
      const { start, end } = action.payload;

      if (!start || !end) return;

      state.newTask.date = start;
      state.newTask.startTime = format(start, 'HH:mm');
      state.newTask.endTime = format(end, 'HH:mm');
    },
  },
  extraReducers: (builder) => {
    builder
      // getContacts
      .addCase(getContacts.pending, (state) => {
        state.newTask.contacts.loading = true;
        state.newTask.contacts.error = null;
      })
      .addCase(getContacts.fulfilled, (state, action) => {
        state.newTask.contacts.loading = false;
        state.newTask.contacts.data = filterDuplicateElements(
          action.payload.contacts,
          state.newTask.contacts.selected
        );
      })
      .addCase(getContacts.rejected, (state) => {
        state.newTask.contacts.loading = false;
        state.newTask.contacts.error = true;
      })

      // getProperties
      .addCase(getProperties.pending, (state) => {
        state.newTask.properties.loading = true;
        state.newTask.properties.error = null;
      })
      .addCase(getProperties.fulfilled, (state, action) => {
        state.newTask.properties.loading = false;
        state.newTask.properties.data = filterDuplicateElements(
          action.payload.properties,
          state.newTask.properties.selected
        );
      })
      .addCase(getProperties.rejected, (state) => {
        state.newTask.properties.loading = false;
        state.newTask.properties.error = true;
      })

      // getTaskFilterProperties
      .addCase(getTaskFilterProperties.pending, (state) => {
        state.filters.properties.loading = true;
        state.filters.properties.error = null;
      })
      .addCase(getTaskFilterProperties.fulfilled, (state, action) => {
        state.filters.properties.loading = false;
        state.filters.properties.data = filterDuplicateElements(
          action.payload.properties,
          state.filters.properties.selected
        );
      })
      .addCase(getTaskFilterProperties.rejected, (state) => {
        state.filters.properties.loading = false;
        state.filters.properties.error = true;
      })

      // getTaskFilterContacts
      .addCase(getTaskFilterContacts.pending, (state) => {
        state.filters.contacts.loading = true;
        state.filters.contacts.error = null;
      })
      .addCase(getTaskFilterContacts.fulfilled, (state, action) => {
        state.filters.contacts.loading = false;
        state.filters.contacts.data = filterDuplicateElements(
          action.payload.contacts,
          state.filters.contacts.selected
        );
      })
      .addCase(getTaskFilterContacts.rejected, (state) => {
        state.filters.contacts.loading = false;
        state.filters.contacts.error = true;
      })

      // getTaskFilterAgents
      .addCase(getTaskFilterAgents.pending, (state) => {
        state.filters.agents.loading = true;
        state.filters.agents.error = null;
      })
      .addCase(getTaskFilterAgents.fulfilled, (state, action) => {
        state.filters.agents.loading = false;
        state.filters.agents.data = filterDuplicateElements(
          action.payload.users,
          state.filters.agents.selected
        );
      })
      .addCase(getTaskFilterAgents.rejected, (state) => {
        state.filters.agents.loading = false;
        state.filters.agents.error = true;
      })

      // getAgents
      .addCase(getAgents.pending, (state) => {
        state.newTask.agents.loading = true;
        state.newTask.agents.error = null;
      })
      .addCase(getAgents.fulfilled, (state, action) => {
        state.newTask.agents.loading = false;
        state.newTask.agents.data = filterDuplicateElements(
          action.payload.users,
          state.newTask.agents.selected
        );
      })
      .addCase(getAgents.rejected, (state) => {
        state.newTask.agents.loading = false;
        state.newTask.agents.error = true;
      })

      // createTask
      .addCase(createTask.pending, (state) => {
        state.newTask.loading = true;
        state.newTask.error = false;
      })
      .addCase(createTask.fulfilled, (state) => {
        state.showModal = false;
        state.newTask = initialState.newTask;
        state.originalNewTask = null;
        state.refreshCount += 1;
      })
      .addCase(createTask.rejected, (state) => {
        state.newTask.loading = false;
        state.newTask.error = true;
      })

      // editTask
      .addCase(editTask.pending, (state) => {
        state.newTask.loading = true;
        state.newTask.error = false;
      })
      .addCase(editTask.fulfilled, (state) => {
        state.showModal = false;
        state.newTask = initialState.newTask;
        state.originalNewTask = null;
        state.refreshCount += 1;
      })
      .addCase(editTask.rejected, (state) => {
        state.newTask.loading = false;
        state.newTask.error = true;
      })

      // getRightbarTasks
      .addCase(getRightbarTasks.pending, (state) => {
        state.rightbar.loading = true;
        state.rightbar.error = false;
      })
      .addCase(getRightbarTasks.fulfilled, (state, action) => {
        const { today, to_review } = action.payload;

        state.rightbar.loading = false;
        state.rightbar.error = false;

        state.rightbar.today = today;
        state.rightbar.to_review = to_review;
        state.rightbar.hasInitialData =
          state.rightbar.hasInitialData ||
          today.length > 0 ||
          to_review.length > 0;
      })
      .addCase(getRightbarTasks.rejected, (state) => {
        state.rightbar.loading = false;
        state.rightbar.error = true;
      })

      // completeTaskFromRightbar
      .addCase(completeTaskFromRightbar.pending, (state, action) => {
        const { id: taskId } = action.meta.arg;
        const { today, to_review } = state.rightbar;

        state.rightbar.today = markCompleting(today, taskId);
        state.rightbar.to_review = markCompleting(to_review, taskId);
      })
      .addCase(completeTaskFromRightbar.fulfilled, (state, action) => {
        const { id: taskId } = action.meta.arg;
        const { today, to_review } = state.rightbar;

        state.rightbar.today = removeTaskById(today, taskId);
        state.rightbar.to_review = removeTaskById(to_review, taskId);
        state.refreshCount += 1;
      })
      .addCase(completeTaskFromRightbar.rejected, (state, action) => {
        const { id: taskId } = action.meta.arg;
        const { today, to_review } = state.rightbar;

        state.rightbar.today = unmarkCompleting(today, taskId);
        state.rightbar.to_review = unmarkCompleting(to_review, taskId);
      })

      // changeTaskStatusFromFilteredTasks
      .addCase(changeTaskStatusFromFilteredTasks.pending, (state, action) => {
        const { id: taskId, payload } = action.meta.arg;
        const { pendingItems, completedItems } = state.tabTasks;
        const isCompleting = payload.status === COMPLETED_TASK_STATUS;

        if (isCompleting) {
          state.tabTasks.pendingItems = markCompleting(pendingItems, taskId);
        } else {
          state.tabTasks.completedItems = markUncompleting(
            completedItems,
            taskId
          );
        }
      })
      .addCase(changeTaskStatusFromFilteredTasks.fulfilled, (state, action) => {
        const { id: taskId, payload } = action.meta.arg;
        const { pendingItems, completedItems } = state.tabTasks;
        const isCompleting = payload.status === COMPLETED_TASK_STATUS;

        if (isCompleting) {
          state.tabTasks.pendingItems = removeTaskById(pendingItems, taskId);
        } else {
          state.tabTasks.completedItems = removeTaskById(
            completedItems,
            taskId
          );
        }

        state.refreshCount += 1;
      })
      .addCase(changeTaskStatusFromFilteredTasks.rejected, (state, action) => {
        const { id: taskId, payload } = action.meta.arg;
        const { pendingItems, completedItems } = state.tabTasks;
        const isCompleting = payload.status === COMPLETED_TASK_STATUS;

        if (isCompleting) {
          state.tabTasks.pendingItems = unmarkCompleting(pendingItems, taskId);
        } else {
          state.tabTasks.completedItems = unmarkUncompleting(
            completedItems,
            taskId
          );
        }
      })

      // changeTaskStatusFromAllTasks
      .addCase(changeTaskStatusFromAllTasks.pending, (state, action) => {
        const { id: taskId, payload } = action.meta.arg;
        const { pending, completed } = state.allTasks;
        const isCompleting = payload.status === COMPLETED_TASK_STATUS;

        if (isCompleting) {
          state.allTasks.pending.items = markCompleting(pending.items, taskId);
        } else {
          state.allTasks.completed.items = markUncompleting(
            completed.items,
            taskId
          );
        }
      })
      .addCase(changeTaskStatusFromAllTasks.fulfilled, (state, action) => {
        const { id: taskId, payload } = action.meta.arg;
        const { pending, completed } = state.allTasks;
        const isCompleting = payload.status === COMPLETED_TASK_STATUS;

        if (isCompleting) {
          state.allTasks.pending.items = removeTaskById(pending.items, taskId);
        } else {
          state.allTasks.completed.items = removeTaskById(
            completed.items,
            taskId
          );
        }

        state.refreshCount += 1;
      })
      .addCase(changeTaskStatusFromAllTasks.rejected, (state, action) => {
        const { id: taskId, payload } = action.meta.arg;
        const { pending, completed } = state.allTasks;
        const isCompleting = payload.status === COMPLETED_TASK_STATUS;

        if (isCompleting) {
          state.allTasks.pending.items = unmarkCompleting(
            pending.items,
            taskId
          );
        } else {
          state.allTasks.completed.items = unmarkUncompleting(
            completed.items,
            taskId
          );
        }
      })

      // getPaginatedFilteredTasks
      .addCase(getPaginatedFilteredTasks.pending, (state, action) => {
        const isAppend = action.meta.arg?.append;

        if (isAppend) {
          state.tabTasks.loadingMore = true;
        } else {
          state.tabTasks.loading = true;
          state.tabTasks.pendingItems = [];
          state.tabTasks.completedItems = [];
          state.tabTasks.loadingMore = false;
        }

        state.tabTasks.error = false;
      })
      .addCase(getPaginatedFilteredTasks.fulfilled, (state, action) => {
        const { tasks, pending, completed, ...restOfResponse } = action.payload;
        const isAppend = action.meta.arg?.append;
        let pendingItems = [];
        const completedItems = normalizeTaskSections(completed);

        if (pending) {
          pendingItems = normalizeTaskSections([
            ...(pending.with_time ?? []),
            ...(pending.without_time ?? []),
          ]);
        } else {
          pendingItems = normalizeTaskSections(tasks);
        }

        state.tabCounts = restOfResponse.tab_counts ?? state.tabCounts;
        state.tabTasks = {
          ...state.tabTasks,
          ...restOfResponse,
          loading: false,
          loadingMore: false,
          error: false,
          pendingItems: isAppend
            ? appendTaskSections(state.tabTasks.pendingItems, pendingItems)
            : pendingItems,
          completedItems: isAppend
            ? appendTaskSections(state.tabTasks.completedItems, completedItems)
            : completedItems,
        };
      })
      .addCase(getPaginatedFilteredTasks.rejected, (state) => {
        state.tabTasks.loading = false;
        state.tabTasks.loadingMore = false;
        state.tabTasks.error = true;
      })

      // getPaginatedAllTaskSection
      .addCase(getPaginatedAllTaskSection.pending, (state, action) => {
        const { sectionKey } = action.meta.arg;
        state.allTasks[sectionKey].loadingMore = true;
        state.allTasks.error = false;
      })
      .addCase(getPaginatedAllTaskSection.fulfilled, (state, action) => {
        const { sectionKey, response } = action.payload;
        const section = state.allTasks[sectionKey];

        state.tabCounts = response.tab_counts ?? state.tabCounts;
        section.loadingMore = false;
        section.pageInfo = response.page_info ?? section.pageInfo;
        section.initialized = true;

        section.items = appendTaskSections(
          section.items,
          getResponseTaskSections(response)
        );
      })
      .addCase(getPaginatedAllTaskSection.rejected, (state, action) => {
        const { sectionKey } = action.meta.arg;

        state.allTasks[sectionKey].loadingMore = false;

        state.allTasks.error = true;
      })

      // getAgentConflicts
      .addCase(getAgentConflicts.pending, () => {
        // ...
      })
      .addCase(getAgentConflicts.fulfilled, (state, action) => {
        state.newTask.conflictEvents = action.payload;
        // ...
      })
      .addCase(getAgentConflicts.rejected, () => {
        // ...
      })

      // getTaskFilterPropertyById
      .addCase(getTaskFilterPropertyById.pending, () => {
        // ...
      })
      .addCase(getTaskFilterPropertyById.fulfilled, (state, action) => {
        const property = action.payload.properties?.[0];
        if (property) {
          state.filters.properties.selected = [
            ...state.filters.properties.selected,
            {
              id: property.id,
              address: property.address ?? property.name ?? '',
            },
          ];
          state.filters.open = true;
        }
      })
      .addCase(getTaskFilterPropertyById.rejected, () => {
        // ...
      })

      // getTaskFilterContactById
      .addCase(getTaskFilterContactById.pending, () => {
        // ...
      })
      .addCase(getTaskFilterContactById.fulfilled, (state, action) => {
        const contact = action.payload.contacts?.[0];
        if (contact) {
          state.filters.contacts.selected = [
            ...state.filters.contacts.selected,
            { id: contact.id, name: contact.name ?? '' },
          ];
          state.filters.open = true;
        }
      })
      .addCase(getTaskFilterContactById.rejected, () => {
        // ...
      });
  },
});

export const {
  setTasks,
  resetModalTask,
  resetAllTasks,
  setNewTaskContextualData,
  showModalTask,
  selectTaskTab,
  showModalTaskToEdit,
  setNewTaskTimeRange,
} = TasksSlice.actions;

export default TasksSlice.reducer;
